import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { useLazyLoadEnabled } from '~SpectateStore/hooks/clientStateHooks';

import './LazyLoad.scss';

export const LAZY_LOAD_DEFAULT_HEIGHT = 100;
export const LAZY_LOAD_DEFAULT_BOTTOM_MARGIN = 100;

const LAZY_LOAD_BASE_CLASS = 'LazyLoad';
const LazyLoadContext = React.createContext({
    lazyLoadHasLoaded: () => true,
    lazyLoadIsEmpty: () => true,
});

/**
 * Custom hook to use the LazyLoadContext.
 * @returns {React.Context} The context object
 */
export const useLazyLoadContext = () => useContext(LazyLoadContext);

/**
 * HOC to provide the LazyLoadContext as props to the wrapped component.
 * It will receive two new props:
 *  - lazyLoadHasLoaded
 *  - lazyLoadIsEmpty
 * These are functions to be called when the widget has finished loading data.
 * Call lazyLoadHasLoaded when the widget has data to display.
 * Call lazyLoadIsEmpty when the widget is empty so LazyLoad can remove the placeholder.
 *
 * @returns {React.Component} The wrapped component
 */
export const withLazyLoadContext = () => (WrappedComponent) => {
    return function WithLazyLoadContext(props) {
        const { lazyLoadHasLoaded, lazyLoadIsEmpty } = useLazyLoadContext();

        return (
            <WrappedComponent
                lazyLoadHasLoaded={lazyLoadHasLoaded}
                lazyLoadIsEmpty={lazyLoadIsEmpty}
                {...props}
            />
        );
    };
};

/**
 * HOC to give a component the lazy loading functionality.
 * A lazy loaded component will only rendered when it becomes visible on the viewport (considering also the treshold).
 *
 * The functionality can be disabled by setting the state value of 'lazyload:enabled' to false, in which case
 * the wrapped componend will be rendered regardless of being visible or not.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * @uses react-intersection-observer
 * @param {Number} height The estimated height of the component
 * @param {Boolean} shouldHideWhileLoading if true the content will use `display: none;` while loading (Should be used for components with fixed height e.g. Made for You Widget). **Note:** the placeholder will still be displayed.
 * @returns {React.Component} The wrapped component that will lazy load
 */
export const withLazyLoad = (
        height = LAZY_LOAD_DEFAULT_HEIGHT,
        shouldHideWhileLoading = false,
    ) => (WrappedComponent) => {

    return function LazyLoad(props) {
        const isLazyLoadEnabled = useLazyLoadEnabled();

        const [ ref, inView ] = useInView({
            triggerOnce: true,
            rootMargin: `0px 0px ${LAZY_LOAD_DEFAULT_BOTTOM_MARGIN}px 0px`,
        });

        const [ isLoaded, setIsLoaded ] = useState(false);
        const [ isEmpty, setIsEmpty ] = useState(false);

        if (!isLazyLoadEnabled) {
            return <WrappedComponent {...props} />;
        }

        const lazyLoadContext = {
            lazyLoadHasLoaded: () => setIsLoaded(true),
            lazyLoadIsEmpty: () => setIsEmpty(true),
        };

        if (isEmpty) {
            return null;
        }

        const showPlaceholder = () => {
            const placeholderClassName = `${LAZY_LOAD_BASE_CLASS}__Placeholder`;
            const cssVar = {
                '--placeholder-height': `${height}px`,
            };

            return (
                <div
                    className={placeholderClassName}
                    style={cssVar}
                ></div>
            );
        };

        const componentWrapperClassNames = classNames(
            `${LAZY_LOAD_BASE_CLASS}__ComponentWrapper`,
            {
                [`${LAZY_LOAD_BASE_CLASS}__ComponentWrapper--loaded`]: isLoaded,
                [`${LAZY_LOAD_BASE_CLASS}__ComponentWrapper--hidden`]: !isLoaded && shouldHideWhileLoading,
            },
        );

        return (
            <div ref={ref} className={LAZY_LOAD_BASE_CLASS}>
                { !isLoaded && showPlaceholder() }
                { inView &&
                    <div className={componentWrapperClassNames}>
                        <LazyLoadContext.Provider value={lazyLoadContext}>
                            <WrappedComponent {...props} />
                        </LazyLoadContext.Provider>
                    </div>
                }
            </div>
        );
    };
};

withLazyLoad.propTypes = {
    height: PropTypes.number,
    shouldHideWhileLoading: PropTypes.bool,
};

export default withLazyLoad;
